import { useState, useEffect } from "react";
import { useTracking } from "@/contexts/TrackingContext";
import { getVisitorId, getSessionId, getAttribution, getCurrentUrlAttribution } from "@/lib/tracker";
import { Bug, X, ChevronDown, ChevronUp, Eye, EyeOff } from "lucide-react";

/**
 * Admin-only debug panel that shows tracking state on any page.
 * Activated via URL param ?debug=tracking or localStorage flag.
 */
export default function TrackingDebugPanel() {
  const { consent, debugEvents, currentBusiness } = useTracking();
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [showEvents, setShowEvents] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const debugFlag = params.get("debug") === "tracking" || localStorage.getItem("ahp_debug") === "1";
    setIsVisible(debugFlag);
  }, []);

  if (!isVisible) return null;

  const visitorId = getVisitorId();
  const sessionId = getSessionId();
  const attribution = getAttribution();
  const urlParams = getCurrentUrlAttribution();
  const hasGtag = typeof window.gtag === "function";
  const hasFbq = typeof window.fbq === "function";
  const hasDataLayer = Array.isArray(window.dataLayer);

  const pixelStatus = currentBusiness?.tracking || currentBusiness?.pixels || {};

  return (
    <div className="fixed bottom-4 right-4 z-[99999]" data-testid="tracking-debug-panel">
      {!isOpen ? (
        <button
          onClick={() => setIsOpen(true)}
          className="bg-[#7C3AED] text-white p-3 rounded-full shadow-lg hover:bg-[#6D28D9] transition-colors"
          title="Open Tracking Debug"
        >
          <Bug className="w-5 h-5" />
        </button>
      ) : (
        <div className="bg-[#0F172A] border border-[#7C3AED]/30 rounded-xl shadow-2xl w-[420px] max-h-[80vh] overflow-y-auto text-xs">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 sticky top-0 bg-[#0F172A] z-10">
            <div className="flex items-center gap-2">
              <Bug className="w-4 h-4 text-[#7C3AED]" />
              <span className="text-white font-bold text-sm">Tracking Debug</span>
            </div>
            <button onClick={() => setIsOpen(false)} className="text-[#94A3B8] hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="p-4 space-y-4">
            {/* Identity */}
            <Section title="Identity">
              <Row label="Visitor ID" value={visitorId} />
              <Row label="Session ID" value={sessionId} />
            </Section>

            {/* Consent */}
            <Section title="Consent Status">
              <Row label="Analytics" value={consent.analytics ? "Granted" : "Denied"} color={consent.analytics ? "green" : "red"} />
              <Row label="Marketing" value={consent.marketing ? "Granted" : "Denied"} color={consent.marketing ? "green" : "red"} />
              <Row label="Interacted" value={consent.hasInteracted ? "Yes" : "No"} />
            </Section>

            {/* Listing Context */}
            {currentBusiness && (
              <Section title="Current Listing">
                <Row label="Listing ID" value={currentBusiness.id || "—"} />
                <Row label="Name" value={currentBusiness.name || "—"} />
              </Section>
            )}

            {/* Pixel Status */}
            <Section title="Pixels / Scripts">
              <Row label="gtag()" value={hasGtag ? "Loaded" : "Missing"} color={hasGtag ? "green" : "yellow"} />
              <Row label="fbq()" value={hasFbq ? "Loaded" : "Missing"} color={hasFbq ? "green" : "yellow"} />
              <Row label="dataLayer" value={hasDataLayer ? `${window.dataLayer.length} entries` : "Missing"} color={hasDataLayer ? "green" : "red"} />
              {Object.entries(pixelStatus).map(([k, v]) => (
                v ? <Row key={k} label={k} value={typeof v === 'string' ? (v.length > 20 ? v.slice(0, 20) + "..." : v) : "Set"} color="green" /> : null
              ))}
            </Section>

            {/* Attribution */}
            <Section title="Attribution">
              <Row label="URL Params" value={Object.keys(urlParams).length > 0 ? JSON.stringify(urlParams) : "None"} />
              <Row label="First Touch" value={attribution.first_touch ? (attribution.first_touch.utm_source || attribution.first_touch.referrer || "Direct") : "None"} />
              <Row label="Last Touch" value={attribution.last_touch ? (attribution.last_touch.utm_source || attribution.last_touch.referrer || "Direct") : "None"} />
              <Row label="Referrer" value={document.referrer || "Direct"} />
            </Section>

            {/* Recent Events */}
            <div>
              <button onClick={() => setShowEvents(!showEvents)} className="flex items-center gap-2 text-white font-semibold mb-2 w-full">
                {showEvents ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                Events Fired ({debugEvents.length})
              </button>
              {showEvents && (
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {debugEvents.length === 0 && <p className="text-[#64748B]">No events fired yet</p>}
                  {debugEvents.map((ev, i) => (
                    <div key={i} className="bg-white/5 rounded px-2 py-1.5 flex items-center justify-between">
                      <div>
                        <span className="text-[#7C3AED] font-mono">{ev.event_name}</span>
                        {ev.listing_id && <span className="text-[#64748B] ml-2">#{ev.listing_id?.slice(0, 8)}</span>}
                      </div>
                      <span className="text-[#475569]">{new Date(ev.timestamp).toLocaleTimeString()}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h3 className="text-[#94A3B8] font-semibold mb-1.5 uppercase tracking-wider" style={{ fontSize: '10px' }}>{title}</h3>
      <div className="bg-white/5 rounded-lg p-2 space-y-1">{children}</div>
    </div>
  );
}

function Row({ label, value, color }) {
  const colorClass = color === "green" ? "text-[#10B981]" : color === "red" ? "text-red-400" : color === "yellow" ? "text-[#F59E0B]" : "text-[#E2E8F0]";
  return (
    <div className="flex items-center justify-between">
      <span className="text-[#64748B]">{label}</span>
      <span className={`font-mono ${colorClass} max-w-[220px] truncate text-right`}>{value}</span>
    </div>
  );
}
